import React from 'react';
import {
  Card,
  Container,
  Divider,
  Heading,
  PageHeader,
  ScaleControl,
  Section,
  Text,
  TextField,
  ThemeToggle,
  Select,
  VisuallyHidden,
} from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import {
  getUploadMaxConcurrent,
  getUploadLaunchIntervalMs,
  setUploadMaxConcurrent,
  setUploadLaunchIntervalMs,
} from '@/utils/uploadPreferences';
import {
  getValidationBatchSize,
  getValidationMaxConcurrent,
  setValidationBatchSize,
  setValidationMaxConcurrent,
} from '@/utils/validationPreferences';

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [uploadMaxConcurrent, setUploadMaxConcurrentState] = React.useState(
    getUploadMaxConcurrent()
  );
  const [uploadLaunchIntervalMs, setUploadLaunchIntervalMsState] =
    React.useState(getUploadLaunchIntervalMs());
  const [validationMaxConcurrent, setValidationMaxConcurrentState] =
    React.useState(getValidationMaxConcurrent());
  const [validationBatchSize, setValidationBatchSizeState] = React.useState(
    getValidationBatchSize()
  );

  React.useEffect(() => {
    // Keep UI in sync when the value changes in another tab/window.
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'upload.maxConcurrent') {
        setUploadMaxConcurrentState(getUploadMaxConcurrent());
        return;
      }
      if (event.key === 'upload.launchIntervalMs') {
        setUploadLaunchIntervalMsState(getUploadLaunchIntervalMs());
        return;
      }
      if (event.key === 'validation.maxConcurrent') {
        setValidationMaxConcurrentState(getValidationMaxConcurrent());
        return;
      }
      if (event.key === 'validation.batchSize') {
        setValidationBatchSizeState(getValidationBatchSize());
        return;
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (!user) {
    return (
      <Container>
        <Section>
          <Text>Loading user data...</Text>
        </Section>
      </Container>
    );
  }

  return (
    <Container py="8">
      <PageHeader title="Personal Settings" size="large" />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {/* Personal Information */}
        <Card>
          <Heading as="h2" size="medium" style={{ marginBottom: '1.5rem' }}>
            Personal Information
          </Heading>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
              }}
            >
              <TextField
                label="Full Name"
                defaultValue={`${user.first_name} ${user.last_name}`}
                placeholder="Enter your full name"
                disabled
              />
              <TextField
                label="Email"
                type="email"
                disabled
                defaultValue={user.email}
                placeholder="Enter your email"
              />
            </div>
          </div>
        </Card>

        {/* Preferences */}
        <Card>
          <Heading as="h2" size="medium" style={{ marginBottom: '1.5rem' }}>
            Preferences
          </Heading>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
          >
            <ThemeToggle
              variant="settings"
              showSystem={true}
              showInterfaceTheme={true}
            />
            <Divider />
            <ScaleControl variant="settings" />
            <Divider />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <Text weight="medium">Parallel uploads</Text>
                <Text size="3xsmall" color="secondary">
                  Higher values may stress the server or cause throttling (429).
                  Recommended: 3–5.
                </Text>
              </div>
              <div style={{ minWidth: 120 }}>
                <Select
                  value={String(uploadMaxConcurrent)}
                  onValueChange={(value: string) => {
                    const next = Number.parseInt(value, 10);
                    setUploadMaxConcurrent(next);
                    setUploadMaxConcurrentState(getUploadMaxConcurrent());
                  }}
                  options={[
                    { value: '1', label: '1' },
                    { value: '2', label: '2' },
                    { value: '3', label: '3' },
                    { value: '4', label: '4' },
                    { value: '5', label: '5' },
                    { value: '8', label: '8' },
                    { value: '10', label: '10' },
                  ]}
                />
              </div>
            </div>
            <Divider />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <Text weight="medium">Upload start interval</Text>
                <Text size="3xsmall" color="secondary">
                  Delay between starting uploads (reduces request bursts).
                </Text>
              </div>
              <div style={{ minWidth: 120 }}>
                <Select
                  value={String(uploadLaunchIntervalMs)}
                  onValueChange={(value: string) => {
                    const next = Number.parseInt(value, 10);
                    setUploadLaunchIntervalMs(next);
                    setUploadLaunchIntervalMsState(getUploadLaunchIntervalMs());
                  }}
                  options={[
                    { value: '0', label: 'No delay' },
                    { value: '100', label: '100 ms' },
                    { value: '250', label: '250 ms' },
                    { value: '500', label: '500 ms' },
                    { value: '1000', label: '1000 ms' },
                  ]}
                />
              </div>
            </div>
            <Divider />
            <VisuallyHidden>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <Text weight="medium">Parallel validations</Text>
                  <Text size="3xsmall" color="secondary">
                    Controls how many validation requests can run at the same time.
                    Recommended: 2–4.
                  </Text>
                </div>
                <div style={{ minWidth: 120 }}>
                  <Select
                    value={String(validationMaxConcurrent)}
                    onValueChange={(value: string) => {
                      const next = Number.parseInt(value, 10);
                      setValidationMaxConcurrent(next);
                      setValidationMaxConcurrentState(getValidationMaxConcurrent());
                    }}
                    options={[
                      { value: '1', label: '1' },
                      { value: '2', label: '2' },
                      { value: '3', label: '3' },
                      { value: '4', label: '4' },
                      { value: '5', label: '5' },
                      { value: '8', label: '8' },
                      { value: '10', label: '10' },
                    ]}
                  />
                </div>
              </div>
              <Divider />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <Text weight="medium">Validation batch size</Text>
                  <Text size="3xsmall" color="secondary">
                    Controls how many files are validated per request. Larger batches
                    reduce request count but increase payload size.
                  </Text>
                </div>
                <div style={{ minWidth: 120 }}>
                  <Select
                    value={String(validationBatchSize)}
                    onValueChange={(value: string) => {
                      const next = Number.parseInt(value, 10);
                      setValidationBatchSize(next);
                      setValidationBatchSizeState(getValidationBatchSize());
                    }}
                    options={[
                      { value: '1000', label: '1,000' },
                      { value: '5000', label: '5,000' },
                      { value: '10000', label: '10,000' },
                    ]}
                  />
                </div>
              </div>
            </VisuallyHidden>
          </div>
        </Card>

        {/* Account Information */}
        <Card>
          <Heading as="h2" size="medium" style={{ marginBottom: '1.5rem' }}>
            Account Information
          </Heading>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text weight="medium">User ID:</Text>
              <Text color="secondary">{user.id}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text weight="medium">Email:</Text>
              <Text color="secondary">{user.email}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text weight="medium">Roles:</Text>
              <Text color="secondary">
                {user.roles?.length
                  ? user.roles
                      .map(role =>
                        role && typeof role === 'object' && 'name' in role
                          ? String((role as { name?: unknown }).name)
                          : ''
                      )
                      .filter(Boolean)
                      .join(', ')
                  : 'No roles assigned'}
              </Text>
            </div>
          </div>
        </Card>
      </div>
    </Container>
  );
};

export default SettingsPage;
